/**
 * TanStack Query bindings for the API client (MASTER_PLAN M2.10, ADR-007).
 *
 * ## Persistence is off, and stays off
 *
 * No persister may ever be installed on a query client that holds these results.
 * Anything TanStack Query writes to `localStorage` or IndexedDB would put patient data
 * in the browser's storage, which ADR-007 and SECURITY.md §7 forbid. The cache is
 * memory-only and dies with the tab; `apps/patient/src/main.tsx` carries the same note
 * over the `QueryClient` it creates.
 *
 * ## One session query, keyed `['session']`
 *
 * Every screen that needs to know who is signed in reads the same cache entry, so a
 * sign-in or a sign-out is a single invalidation and not a fan-out of refetches.
 * `staleTime` is 30 s: long enough that navigating between routes does not re-ask, short
 * enough that a session revoked elsewhere surfaces quickly - and the API is the actual
 * enforcement point either way (rules §17.2).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { ApiClient, SessionSummary } from './client.js';
import type { ApiError } from './errors.js';

/** The cache key for the session summary. Exported so tests and guards can seed it. */
export const SESSION_QUERY_KEY = ['session'] as const;

/** 30 seconds (ADR-007). */
export const SESSION_STALE_TIME_MS = 30_000;

const ApiClientContext = createContext<ApiClient | null>(null);

export interface ApiClientProviderProps {
  client: ApiClient;
  children: ReactNode;
}

export function ApiClientProvider({ client, children }: ApiClientProviderProps) {
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/** The client for this tree. Throws rather than falling back to a second client. */
export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (client === null) {
    throw new Error('useApiClient must be used inside <ApiClientProvider>.');
  }
  return client;
}

/** The anonymous summary, for optimistic writes after a sign-out. */
export const ANONYMOUS_SESSION: SessionSummary = {
  authenticated: false,
  user: null,
  tenant: null,
  permissions: [],
  mfa: null,
  impersonation: null,
  expiresAt: null,
};

/** The query definition, so a route loader can prefetch the same entry the hook reads. */
export function sessionQueryOptions(
  client: ApiClient,
): UseQueryOptions<SessionSummary, ApiError, SessionSummary, typeof SESSION_QUERY_KEY> {
  return {
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => client.getSession({ signal }),
    staleTime: SESSION_STALE_TIME_MS,
    /*
     * `GET /auth/session` is always 200, so a rejection here is a network or server
     * fault, not "signed out". One retry matches the app's default and keeps a flaky
     * first paint from bouncing a signed-in patient to /login.
     */
    retry: 1,
  };
}

/** Who is signed in, which clinic this is, and what they may see. */
export function useSession(): UseQueryResult<SessionSummary, ApiError> {
  const client = useApiClient();
  const options = useMemo(() => sessionQueryOptions(client), [client]);
  return useQuery(options);
}

/**
 * Signs out and empties the cache.
 *
 * `clear()` rather than `invalidateQueries()`: after a sign-out every cached answer was
 * fetched for a person who is no longer here, and leaving them in memory for the next
 * visitor on a shared device is exactly the failure mode session expiry exists to
 * prevent. The session entry is then seeded with the anonymous summary so the shell
 * re-renders as signed out without a round trip.
 */
export function useSignOut(): UseMutationResult<void, ApiError, void> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, void>({
    mutationFn: () => client.auth.signOut(),
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(SESSION_QUERY_KEY, ANONYMOUS_SESSION);
    },
  });
}
